import { getBackendUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight, 
  Loader2, 
  CheckCircle, 
  ClipboardList,
  Send,
  Clock,
  AlertCircle
} from 'lucide-react';
import SeoHead from '../components/SeoHead';

const ApplyWithTokenPage = () => {
  const { role, token } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Form navigation
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    fetchForm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, token]);

  const fetchForm = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/public/apply/${role}/${token}`);
      
      if (response.status === 410) {
        setError('expired');
        setLoading(false);
        return;
      }
      
      if (response.status === 404) {
        setError('invalid');
        setLoading(false);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setFormData(data);
        
        // Pré-remplir les réponses vides
        const initialAnswers = {};
        data.questions.forEach(q => {
          initialAnswers[q.id] = '';
        });
        setAnswers(initialAnswers);
      } else {
        setError('unknown');
      }
    } catch (err) {
      console.error('Error fetching form:', err);
      setError('network');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const getCurrentSectionQuestions = () => {
    if (!formData || !formData.sections) return [];
    const section = formData.sections[currentSection];
    return formData.questions.filter(q => q.section_id === section.id);
  };

  const validateCurrentSection = () => {
    const questions = getCurrentSectionQuestions();
    for (const q of questions) {
      if (!answers[q.id]?.trim()) {
        toast.error('Veuillez répondre à toutes les questions de cette section');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentSection()) return;
    setCurrentSection(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    setCurrentSection(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateCurrentSection()) return;

    setSubmitting(true);
    try {
      // Formater les réponses avec les questions
      const formattedAnswers = formData.questions.map(q => ({
        question_id: q.id,
        question: q.question,
        section: q.section_title,
        answer: answers[q.id] || ''
      }));

      const response = await fetch(`${getBackendUrl()}/api/public/apply/${role}/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedAnswers)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la soumission');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const getProgress = () => {
    if (!formData?.sections) return 0;
    return Math.round(((currentSection + 1) / formData.sections.length) * 100);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tp-primary" />
      </div>
    );
  }

  // Error states
  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <Clock className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Lien expiré</h2>
          <p className="text-gray-600 mb-4">
            Ce lien de qualification a expiré ou a déjà été utilisé.
          </p>
          <Link to="/">
            <Button>Retour à l'accueil</Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (error === 'invalid' || error === 'unknown' || error === 'network') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Lien invalide</h2>
          <p className="text-gray-600 mb-4">
            Ce lien n'est pas valide. Veuillez vérifier l'URL ou contacter l'équipe.
          </p>
          <Link to="/">
            <Button>Retour à l'accueil</Button>
          </Link>
        </Card>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <SeoHead pageTitle="Évaluation soumise | Talent Partner" />
        <Card className="p-8 text-center max-w-lg">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-display font-semibold text-gray-900 mb-4">
            Merci pour votre candidature !
          </h2>
          <p className="text-gray-600 mb-6">
            Votre évaluation a été soumise avec succès. Notre équipe va analyser votre profil 
            et reviendra vers vous très prochainement.
          </p>
          <Link to="/">
            <Button className="w-full bg-tp-primary hover:bg-tp-primary-light">
              Retour à l'accueil
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  // Main form
  const currentSectionData = formData?.sections?.[currentSection];
  const currentQuestions = getCurrentSectionQuestions();
  const isLastSection = currentSection === (formData?.sections?.length || 1) - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SeoHead pageTitle={`${formData?.title || 'Évaluation'} | Talent Partner`} />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-tp-primary font-display font-semibold">
              <span className="text-xl">Talent Partner</span>
            </Link>
            <Badge variant="secondary" className="text-xs">
              Section {currentSection + 1}/{formData?.sections?.length || 1}
            </Badge>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4">
          <Progress value={getProgress()} className="h-1.5" />
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Title Card - Only on first section */}
        {currentSection === 0 && (
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-tp-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="h-8 w-8 text-tp-primary" />
            </div>
            <h1 className="text-2xl md:text-3xl font-display font-semibold text-gray-900 mb-3">
              {formData?.title}
            </h1>
            {formData?.candidate?.first_name && (
              <p className="text-tp-primary font-medium mb-2">
                Bonjour {formData.candidate.first_name} !
              </p>
            )}
            <p className="text-gray-600 max-w-xl mx-auto">
              {formData?.description}
            </p>
            <p className="text-sm text-gray-500 mt-4">
              {formData?.questions?.length} questions • 4 sections • Durée : 20-30 minutes
            </p>
          </div>
        )}

        <Card className="p-6 md:p-8">
          {/* Section Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-display font-semibold text-gray-900">
                {currentSectionData?.title}
              </h2>
              <span className="text-sm text-gray-500">
                {currentQuestions.length} questions
              </span>
            </div>
            {currentSectionData?.description && (
              <p className="text-sm text-gray-600">{currentSectionData.description}</p>
            )}
          </div>

          {/* Questions */}
          <div className="space-y-8">
            {currentQuestions.map((question, idx) => (
              <div key={question.id} className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-tp-primary/10 text-tp-primary text-sm font-medium flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <label className="text-gray-900 font-medium leading-relaxed">
                    {question.question}
                  </label>
                </div>
                <div className="ml-10">
                  <Textarea
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    placeholder="Votre réponse..."
                    rows={4}
                    className="resize-none"
                    data-testid={`answer-${question.id}`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {answers[question.id]?.length || 0} caractères
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            {currentSection > 0 ? (
              <Button 
                variant="outline" 
                onClick={handlePrevious}
                data-testid="btn-previous"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Section précédente
              </Button>
            ) : (
              <div></div>
            )}

            {!isLastSection ? (
              <Button 
                onClick={handleNext}
                className="bg-tp-primary hover:bg-tp-primary-light"
                data-testid="btn-next"
              >
                Section suivante
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="btn-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Soumettre mes réponses
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* Info */}
        <p className="mt-6 text-center text-sm text-gray-500">
          Vos réponses sont analysées par notre système intelligent.
          Prenez le temps de bien répondre.
        </p>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-6">
        <div className="max-w-3xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} Talent Partner. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default ApplyWithTokenPage;
